import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { useSettingsFilters } from '../hooks';
import { taskTypesApi, settingsQueryKeys } from '../api';
import { SettingsDataTable, useSettingsColumns } from '../components';
import { TaskTypeAction } from '../components/TaskTypeAction';
import type { TaskType } from '../types';

export function TaskTypesPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, setPage, setLimit, handleSort } = useSettingsFilters();

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: settingsQueryKeys.taskTypes.list(filters),
    queryFn: () => taskTypesApi.getAll(filters),
  });

  const columns = useSettingsColumns<TaskType>({
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    resource: 'task-type',
    apiEndpoint: 'Task-Types',
    queryKey: 'taskTypes',
    title: t('taskType'),
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('taskType')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('task', 'get') && (
            <Link to="/app/new-tasks">
              <Button variant="outline">{t('tasks')}</Button>
            </Link>
          )}
          {hasAccess('task-type', 'create') && (
            <TaskTypeAction />
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <SettingsDataTable
          tableZoom={tableZoom}
          tableId="task-types"
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          emptyMessage={t('noTaskTypes')}
          createButton={
            hasAccess('task-type', 'create') ? (
              <TaskTypeAction />
            ) : undefined
          }
          extraFilters={
            <Select
              value={filters.is_active === undefined ? 'all' : String(filters.is_active)}
              onValueChange={(val) =>
                updateFilter('is_active', val === 'all' ? undefined : val === 'true', true)
              }
            >
              <SelectTrigger className="w-[180px] h-8 text-xs">
                <SelectValue placeholder={t('all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="true">{t('active')}</SelectItem>
                <SelectItem value="false">{t('inactive')}</SelectItem>
              </SelectContent>
            </Select>
          }
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
